/**
 * @file RepOrderCard.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-30
 * @description One customer order as a card on a rep's phone (Search
 *              Customer Orders). Cam, 28 Oct 2026: "the 'cards' are not
 *              looking right on mobile for this component (overlapping text,
 *              awkward layout, not-intuitive tap areas triggering effects)".
 *
 * Why a card of its own rather than the table restyled.
 *
 * The phone used to restyle the desktop row with CSS: cells pulled into
 * place by absolute positioning and negative margins. That is where the
 * overlap came from — the order number was laid OVER the customer name, so a
 * long name ran under it, and Created / Delivery shared a line only because
 * one was pulled up 14px onto the other. And the row kept the desktop's tap
 * behaviour: a tap anywhere expanded a ten-column line table, while a tap on
 * the name, the account number or the supplier opened a contact instead —
 * three different things, decided by where a thumb happened to land.
 *
 * Here every element sits in normal flow, so nothing can sit on anything
 * else, and there are exactly three things to tap, each saying what it does:
 *  - the card itself opens the order (the chevron says so);
 *  - "Lines" shows the order's lines inside the card;
 *  - "Customer" opens the customer.
 * The status is said in words (utils/repOrderCard) — the eight icons it
 * replaces explained themselves only on hover, which a phone does not have.
 *
 * Read only, like everything a rep sees: it offers no action that writes.
 *
 * 1.1.0 (2026-10-28): an open line is the shared RepLineBlock, so the invoice,
 * PO and order windows show their lines on a phone the same way.
 * 1.2.0 (2026-10-30, Cam — 29 Oct): a fourth tap target, "Change", when
 * `onRequestChange` is given — "Request a change" to the office (a request,
 * never an edit: utils/repOrderRequests). Greyed with the reason on an
 * archived or invoiced order (orderAmendBlock).
 */

import React from 'react';
import QtyWithTonnes from './QtyWithTonnes';
import RepLineBlock from './RepLineBlock';
import { formatPrice } from '../utils/currencyFormatter';
import { addressLine1 } from '../utils/formatAddress';
import { formatDate } from '../utils/dateFormatters';
import {
  deliveryDateParts, repOrderStatus, repPriceState, repDeliveryRoute,
  repOrderFlags, repOrderItems, repLineSource, suppliersText, perUnitText
} from '../utils/repOrderCard';
import { orderAmendBlock } from '../utils/repOrderRequests';
import './RepOrderCard.css';

/** How many of an order's lines the card lists before "+ N more". */
const ITEMS_SHOWN = 3;

const money = (amount) => new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2
}).format(Number(amount) || 0);

function Chip({ tone = 'muted', children }) {
  return <span className={`roc-chip tone-${tone}`}>{children}</span>;
}

/** One order line inside the open card: what, how much, what price, bought where. */
function RepOrderLine({ line, po, onOpenPurchaseOrder }) {
  const name = line.item_name || line.sale_description || line.item_code || 'Line';
  const words = (line.sale_description || '').trim();
  const additives = (Array.isArray(line.additives) ? line.additives : []).map(a => a && a.name).filter(Boolean);
  const sale = Number(line.unit_price);
  const source = repLineSource(line, po);
  return (
    <RepLineBlock
      code={line.item_code}
      name={name}
      words={[words, additives.length > 0 ? `+ ${additives.join(' + ')}` : '']}
      facts={[
        { key: 'ordered', label: 'Ordered', value: <QtyWithTonnes quantity={line.quantity} line={line} /> },
        {
          key: 'supplied',
          label: 'Supplied',
          value: line.supplied_quantity != null
            ? <QtyWithTonnes quantity={line.supplied_quantity} line={line} pallets={null} />
            : null,
          none: 'Not yet'
        },
        {
          key: 'price',
          label: 'Price',
          align: 'right',
          value: sale > 0 ? (
            <span className="roc-fact-value">
              {formatPrice(sale)}
              <span className="roc-fact-per"> {perUnitText(line)}</span>
            </span>
          ) : null,
          none: 'Not priced'
        }
      ]}
    >
      {po ? (
        <button
          type="button"
          data-read-safe
          className="roc-line-po"
          onClick={() => onOpenPurchaseOrder && onOpenPurchaseOrder(po)}
          aria-label={`Open ${po.purchase_order_number}`}
        >
          <span className="material-symbols-rounded" aria-hidden="true">shopping_cart</span>
          <span className="roc-line-po-text">{source}</span>
          <span className="material-symbols-rounded roc-chevron" aria-hidden="true">chevron_right</span>
        </button>
      ) : (
        <div className="roc-line-po is-static">
          <span className="material-symbols-rounded" aria-hidden="true">{line.is_stock ? 'warehouse' : 'pending'}</span>
          <span className="roc-line-po-text">{source}</span>
        </div>
      )}
    </RepLineBlock>
  );
}

function RepOrderCard({
  order,
  items: fallbackItems = [],
  suppliers = [],
  lineRows,
  expanded = false,
  onOpenOrder,
  onToggleLines,
  onOpenCustomer,
  onOpenPurchaseOrder,
  onRequestChange = null
}) {
  const date = deliveryDateParts(order.delivery_date);
  const status = repOrderStatus(order);
  const price = repPriceState(order);
  const route = repDeliveryRoute(order);
  const flags = repOrderFlags(order);
  const items = repOrderItems(order, fallbackItems);
  const shown = items.slice(0, ITEMS_SHOWN);
  const more = items.length - shown.length;
  const place = addressLine1(order.address);
  const supplierText = suppliersText(suppliers);
  const total = Number(order.total_amount) || 0;
  const lineCount = Array.isArray(lineRows) ? lineRows.length : (Number(order.line_count) || 0);
  const linesId = `roc-lines-${order.id}`;

  return (
    <article className={`rep-order-card${expanded ? ' is-open' : ''}${order.is_archived ? ' is-archived' : ''}`}>
      <button
        type="button"
        data-read-safe
        className="roc-main"
        onClick={onOpenOrder}
        aria-label={`Open order ${order.order_number}${order.company_name ? ` for ${order.company_name}` : ''}`}
      >
        <span className="roc-head">
          <span className="roc-who">
            <span className="roc-number">{order.order_number}</span>
            <span className="roc-customer">{order.company_name || 'No customer'}</span>
            {(order.account_no || place) && (
              <span className="roc-place">
                {[order.account_no, place].filter(Boolean).join(' · ')}
              </span>
            )}
          </span>
          <span className="roc-when">
            <span className={`roc-when-label${date.when ? ' is-near' : ''}`}>{date.when || 'Delivery'}</span>
            <span className="roc-when-date">{date.text}</span>
            {order.collected_date && (
              <span className="roc-when-label">Collected {formatDate(order.collected_date)}</span>
            )}
          </span>
          <span className="material-symbols-rounded roc-chevron roc-open" aria-hidden="true">chevron_right</span>
        </span>

        {flags.length > 0 && (
          <span className="roc-flags">
            {flags.map(f => <span key={f.key} className="roc-flag">{f.label}</span>)}
          </span>
        )}

        {shown.length > 0 && (
          <span className="roc-items">
            {shown.map(item => (
              <span className="roc-item" key={item.key}>
                <span className="roc-item-name">{item.name}</span>
                <span className="roc-item-qty">
                  {item.line
                    ? <QtyWithTonnes quantity={item.quantity} line={item.line} />
                    : (item.quantity != null ? <QtyWithTonnes quantity={item.quantity} /> : null)}
                </span>
              </span>
            ))}
            {more > 0 && <span className="roc-more">+ {more} more line{more === 1 ? '' : 's'}</span>}
          </span>
        )}

        {(supplierText || route) && (
          <span className="roc-route">
            {[supplierText && `From ${supplierText}`, route].filter(Boolean).join(' · ')}
          </span>
        )}

        <span className="roc-status">
          <Chip tone={status.tone}>{status.label}</Chip>
          {price && <Chip tone={price.tone}>{price.label}</Chip>}
          <span className={`roc-total${total > 0 ? '' : ' is-empty'}`}>
            {total > 0 ? money(total) : 'No total yet'}
          </span>
        </span>
      </button>

      <div className="roc-actions">
        <button
          type="button"
          data-read-safe
          className="roc-action"
          onClick={onToggleLines}
          aria-expanded={expanded}
          aria-controls={linesId}
          disabled={!lineCount}
          title={lineCount ? undefined : 'This order has no lines'}
        >
          <span className={`material-symbols-rounded roc-toggle${expanded ? ' is-open' : ''}`} aria-hidden="true">expand_more</span>
          {expanded ? 'Hide lines' : `Lines${lineCount ? ` (${lineCount})` : ''}`}
        </button>
        <button
          type="button"
          data-read-safe
          className="roc-action"
          onClick={onOpenCustomer || undefined}
          disabled={!onOpenCustomer}
          title={onOpenCustomer ? undefined : 'No customer account on this order'}
        >
          <span className="material-symbols-rounded" aria-hidden="true">person</span>
          Customer
        </button>
        {onRequestChange && (() => {
          const block = orderAmendBlock(order);
          return (
            <button
              type="button"
              data-read-safe
              className="roc-action"
              onClick={block ? undefined : onRequestChange}
              disabled={!!block}
              title={block || 'Ask the office to change this order'}
            >
              <span className="material-symbols-rounded" aria-hidden="true">edit_note</span>
              Change
            </button>
          );
        })()}
      </div>

      {expanded && (
        <div className="roc-lines" id={linesId}>
          {lineRows && lineRows.length > 0 ? (
            lineRows.map(({ key, line, po }) => (
              <RepOrderLine key={key} line={line} po={po} onOpenPurchaseOrder={onOpenPurchaseOrder} />
            ))
          ) : (
            <div className="roc-lines-empty">
              {lineRows === null
                ? "Could not load this order's lines — search again to retry."
                : 'This order has no lines.'}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default RepOrderCard;
